import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  Building2, ChevronDown, LogOut, MessageCircle, Monitor, Moon, Shield, Sun
} from 'lucide-react'
import { Avatar } from '@/components/design-system'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import OrganizationSwitcher from '@/frontend/components/OrganizationSwitcher'
import { useDropdown } from '@/frontend/hooks/useDropdown'
import { useTheme } from '@/frontend/contexts/ThemeContext'
import type { SharedProps } from '@/frontend/types/inertia'

interface Props {
  onNavigate: () => void
  projectContext?: boolean
}

// Iniciais do quadrado da org: duas letras — primeiras de cada palavra ou os
// dois primeiros caracteres de nomes de uma palavra só.
function orgInitials(name: string | undefined | null): string {
  const trimmed = (name || 'O').trim()
  const parts = trimmed.split(/\s+/)
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase()
  return trimmed.slice(0, 2).toUpperCase()
}

interface OrgNavItem {
  label: string
  href: string
  icon: React.ReactNode
  // Optional exact match (index-only) or extra path prefixes; kept generic so
  // future org-level entries can opt in. Configurações matches by prefix.
  exact?: boolean
  alsoMatchPaths?: string[]
}

/**
 * Footer org/account anchor (SZ-1571, "Tudo no rodapé"). Replaces both the top
 * org block and the static user footer: the trigger row shows the active
 * organization (square avatar + name) and opens a single upward menu.
 *
 * Minimalista desde a Iteração 3 (Ajustes 5+6): as páginas org-level de acesso
 * frequente — Equipe, Integrações e Publicações — subiram para a própria
 * sidebar (WorkerFirstSidebar). Credenciais fica dentro de Integrações, e Auditoria/Processos saíram daqui de
 * vez (são superfícies DO AGENTE — vivem no disclosure, não em org-level).
 * O dropdown fica com o essencial da conta/org: Configurações da organização,
 * trocar organização, Admin (se admin), tema e sair. O email do usuário fica
 * discreto no topo. Behind featureFlags.worker_first_nav_enabled.
 */
export default function OrgAccountMenu({ onNavigate, projectContext = false }: Props) {
  const { props: pageProps, url } = usePage<SharedProps>()
  const auth = pageProps.auth
  const forgeRestricted = pageProps.permissions?.can_access_forge !== true &&
    pageProps.permissions?.access_scope === 'forge_only'
  const menu = useDropdown()
  // Confirmação de logout via AlertDialog do design system (SZ-1589) — o
  // dialog vive fora do dropdown porque o menu fecha antes de confirmar.
  const [confirmingLogout, setConfirmingLogout] = useState(false)
  const { theme, setTheme } = useTheme()
  // Cycles light -> dark -> system (SZ-1582); the row shows the current theme.
  const nextTheme = { light: 'dark', dark: 'system', system: 'light' } as const
  const ThemeIcon = { light: Sun, dark: Moon, system: Monitor }[theme]
  const themeLabel = { light: 'Tema: Claro', dark: 'Tema: Escuro', system: 'Tema: Sistema' }[theme]

  const [path] = url.split('?')
  const organization = auth.user?.active_organization

  // Só as configurações da organização sobraram aqui (Ajustes 5+6): Equipe,
  // Integrações e Publicações moraram para a sidebar; Credenciais fica dentro
  // de Integrações. Processos é do agente (disclosure da sidebar) e Auditoria
  // saiu da navegação (rota viva só por deep-link).
  const items: OrgNavItem[] = [
    { label: 'Configurações', href: `/dashboard/organizations/${organization?.slug || ''}`, icon: <Building2 className="h-4 w-4" strokeWidth={1.75} /> }
  ]

  // A digital_worker_id in the URL means the page is being viewed in worker
  // scope — the worker nav owns the highlight then, not the org pages.
  const workerScoped = new URLSearchParams(url.split('?')[1] ?? '').has('digital_worker_id')
  const isItemActive = (item: OrgNavItem) =>
    !workerScoped &&
    (item.exact
      ? path === item.href
      : url.startsWith(item.href) || (item.alsoMatchPaths?.some((p) => url.startsWith(p)) ?? false))

  return (
    <div className="relative shrink-0 border-t border-border">
      <button
        ref={menu.buttonRef}
        type="button"
        onClick={menu.toggle}
        aria-expanded={menu.isOpen}
        data-testid="wfn-account-trigger"
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-accent"
      >
        {/* Quadrado da org 22px (protótipo): iniciais em s3, sem avatar grande. */}
        <span
          aria-hidden
          className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md bg-secondary text-[9.5px] font-bold text-muted-foreground"
        >
          {orgInitials(projectContext ? auth.user?.name : organization?.name)}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">
          {projectContext ? auth.user?.name || auth.user?.email_address : organization?.name || 'Organização'}
        </span>
        <span className="flex flex-none items-center gap-0.5 text-[11px] text-faint-foreground">
          conta
          <ChevronDown className="h-3 w-3" strokeWidth={1.75} />
        </span>
      </button>

      {menu.isOpen && (
        <div
          data-dropdown
          data-testid="wfn-account-menu"
          className="absolute left-3 right-3 bottom-full mb-2 z-40 bg-card border border-border rounded-xl shadow-lg overflow-hidden"
        >
          <div className="max-h-[62vh] overflow-y-auto py-2">
            {auth.user && (
              <div className="mb-1 flex items-center gap-3 border-b border-border px-4 pb-3 pt-1">
                <Avatar src={auth.user.avatar_url || undefined} name={auth.user.name || auth.user.email_address} size="sm" />
                <div className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{auth.user.name || auth.user.email_address}</span>
                  <span className="block truncate text-xs text-muted-foreground">{auth.user.email_address}</span>
                </div>
              </div>
            )}

            {/* Org-wide pages (moved from the old top org block) */}
            {!forgeRestricted && <div>
              <div className="px-4 py-1.5">
                <span className="text-xs text-muted-foreground font-medium">Organização</span>
              </div>
              <ul className="space-y-0.5 px-2">
                {items.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      onClick={() => { menu.close(); onNavigate() }}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        isItemActive(item)
                          ? 'text-foreground bg-accent font-medium'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>}

            {/* Organization switcher (reused as-is) */}
            {!projectContext && <div className="border-t border-border mt-2 pt-2">
              <div className="px-4 py-1.5">
                <span className="text-xs text-muted-foreground font-medium">Trocar organização</span>
              </div>
              <OrganizationSwitcher onClose={menu.close} isOpen={menu.isOpen} />
            </div>}

            {/* Admin (admin only), theme and logout */}
            <div className="border-t border-border mt-2 pt-2">
              {auth.user?.is_admin && <Link href="/dashboard/evolution_accounts" onClick={() => { menu.close(); onNavigate() }} className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors"><MessageCircle className="mr-3 size-4" />{pageProps.navigationLabels.forge.manage_accounts}</Link>}
              {auth.user?.is_admin && (
                <Link
                  href="/cc"
                  className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors"
                  onClick={() => { menu.close(); onNavigate() }}
                >
                  <Shield className="h-4 w-4 mr-3" strokeWidth={1.75} />
                  <span>Admin</span>
                </Link>
              )}

              <button
                onClick={() => setTheme(nextTheme[theme])}
                title="Mudar tema"
                className="flex items-center px-4 py-2 text-foreground hover:bg-accent transition-colors w-full"
              >
                <ThemeIcon className="h-4 w-4 mr-3" strokeWidth={1.75} />
                <span>{themeLabel}</span>
              </button>
            </div>

            <div className="border-t border-border mt-2 pt-2">
              <button
                onClick={() => {
                  menu.close()
                  setConfirmingLogout(true)
                }}
                className="flex items-center px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors w-full"
              >
                <LogOut className="h-4 w-4 mr-3" strokeWidth={1.75} />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirmingLogout} onOpenChange={setConfirmingLogout}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair da conta?</AlertDialogTitle>
            <AlertDialogDescription>
              Você precisará entrar novamente para acessar a sua organização.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => router.delete('/session', { onSuccess: onNavigate })}>
              Sair
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
